import { GetStaticPathsResult, GetStaticPropsContext } from 'next';

import BlogPosts from '..';
import { getBlogAuthor } from '../../../../../src/api/contentful/blog/getBlogAuthor';
import { getBlogPostsByAuthor } from '../../../../../src/api/contentful/blog/getBlogPostsByAuthor';
import { AppLogic } from '../../../../../src/components/AppLogic/AppLogic';
import { GlobalLayout } from '../../../../../src/components/Layouts/GlobalLayout';
import { BLOG_PAGE_NAV_LINKS, BLOG_POSTS_PER_PAGE } from '../../../../../src/constants/constants';
import { NOT_FOUND_WITH_REVALIDATION, REVALIDATE } from '../../../../../src/constants/next';
import { reportMykissError } from '../../../../../src/helpers/errorHandling';
import { toNumber } from '../../../../../src/helpers/strings/toNumber';
import { getAuthorBlogPostsUrl, getBlogPostsUrl } from '../../../../../src/helpers/urls';
import { StaticResult } from '../../../../../src/interfaces/nextjs';
import { IBlogPostsProps } from '../../../../../src/views/Blog/BlogPosts/BlogPosts';

type IParams = {
  authorSlug: string;
  page: string;
};

export async function getStaticProps({
  params,
}: GetStaticPropsContext<IParams>): StaticResult<IBlogPostsProps> {
  const page = toNumber(params?.page);
  const authorSlug = params?.authorSlug;

  if (!authorSlug || !page) {
    return { notFound: true };
  }

  try {
    const authorDetails = await getBlogAuthor(authorSlug);
    if (!authorDetails) {
      return NOT_FOUND_WITH_REVALIDATION;
    }
    const { items, total } = await getBlogPostsByAuthor(
      page,
      BLOG_POSTS_PER_PAGE,
      authorDetails.sys.id,
    );

    if (page > Math.ceil(total / BLOG_POSTS_PER_PAGE)) {
      return {
        redirect: {
          destination: getAuthorBlogPostsUrl(authorSlug),
          permanent: false,
        },
      };
    }

    return {
      props: {
        _page: 'blog_author_posts',
        _pageTitle: 'Blog Posts by Author',
        _pageId: authorDetails.sys.id,
        _pageSubject: authorDetails.fields.name,
        posts: items,
        page,
        endPage: Math.ceil(total / BLOG_POSTS_PER_PAGE),
        canonicalPath: getBlogPostsUrl(page, getAuthorBlogPostsUrl(authorSlug)),
        title: authorDetails.fields.name,
        type: 'author',
        paginationPath: getAuthorBlogPostsUrl(authorSlug),
      },
      revalidate: REVALIDATE.HOURLY,
    };
  } catch (error) {
    reportMykissError(error);
    return NOT_FOUND_WITH_REVALIDATION;
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult<IParams>> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

BlogPosts.getLayout = function getLayout(page: any) {
  return (
    <AppLogic pageProps={page.props}>
      <GlobalLayout siteSubNavigationLinks={BLOG_PAGE_NAV_LINKS}>{page}</GlobalLayout>
    </AppLogic>
  );
};

export default BlogPosts;
